import { z } from 'zod'

/**
 * Die Präsentation auf der Bühne (docs/SETUP.md §9, „Präsentation“): K4ia
 * führt durch einen Foliensatz und spricht je Folie einen **festen** Text —
 * wörtlich, nichts wird vom Modell umformuliert. Folienbilder und Texte
 * pflegt das Admin; der Inhalt (Businessplan, vertraulich) liegt nur in der
 * Datenbank und in R2, nie im Repository.
 */

export const praesentationFolieSchema = z.object({
  nr: z.number().int().min(1),
  titel: z.string().default(''),
  /** Was K4ia zu dieser Folie spricht; leer = Folie wird nur gezeigt. */
  text: z.string().default(''),
  /** Ist ein Bild hinterlegt? Der Pfad selbst steht nie im Stand, nur im Worker. */
  bild: z.boolean().default(false),
})
export type PraesentationFolie = z.infer<typeof praesentationFolieSchema>

export const PRAESENTATION_ANSPRECHPARTNER = 'Tobias Wahner und Ulrich Gauweiler'

export const praesentationEinstellungenSchema = z.object({
  /** Kurzfassung, die statt der Quartalsfolien gesprochen wird. */
  kurzfassung: z.string().default(''),
  /** Erste und letzte Folie des Bereichs, der übersprungen werden darf (beide einschließlich); null = keiner. */
  quartaleVon: z.number().int().min(1).nullable().default(null),
  quartaleBis: z.number().int().min(1).nullable().default(null),
  /**
   * An wen K4ia bei Bewertung, Beteiligung, Darlehen und Verträgen verweist
   * (Tobi, 18.10.) — so, wie sie es sagen soll („Tobias Wahner und Ulrich Gauweiler“).
   */
  ansprechpartner: z.string().max(200).default(PRAESENTATION_ANSPRECHPARTNER),
  /** Automatik: Pause nach der Tonspur, bevor die nächste Folie kommt. */
  autoPauseSekunden: z.number().min(0).max(30).default(2),
  /** Automatik: wie lange eine Folie ohne Sprechtext stehen bleibt. */
  autoStummSekunden: z.number().min(0).max(60).default(4),
})
export type PraesentationEinstellungen = z.infer<typeof praesentationEinstellungenSchema>

export const PRAESENTATION_PHASEN = ['bereit', 'laeuft', 'fragen', 'fertig'] as const
export type PraesentationPhase = (typeof PRAESENTATION_PHASEN)[number]

/** Was das Handy auslösen kann. */
export const PRAESENTATION_BEFEHLE = [
  /** Von vorn: Folie 1, K4ia spricht. */
  'start',
  'weiter',
  'zurueck',
  /** Zu einer bestimmten Folie springen (`folie`). */
  'springen',
  'pause',
  'fortsetzen',
  /** An der ersten Quartalsfolie: alle Quartalsfolien zeigen … */
  'quartale',
  /** … oder die Kurzfassung sprechen und danach hinter dem Bereich weitermachen. */
  'kurzfassung',
  /** Nach der letzten Folie: Fragen aus dem Publikum. */
  'fragen',
  /** Zurück auf Anfang, Bildschirm zeigt die Startfolie. */
  'ende',
  /** Vom Handy: K4ia klickt nach jeder Folie selbst weiter (Flo, 28.10.) … */
  'automatik_an',
  /** … oder eben nicht mehr. Pause, Zurück und Springen schalten die Automatik auch aus. */
  'automatik_aus',
  /** Vom Bildschirm: die Tonspur ist zu Ende und die Pause vorbei — weiter, wenn die Automatik an ist. */
  'auto_weiter',
] as const
export type PraesentationBefehlArt = (typeof PRAESENTATION_BEFEHLE)[number]

/**
 * Das Skript, wie es die Gründer liefern (Flo, 18.10.): Abschnitte mit
 * „Folie 12 – Titel“, darunter der Sprechtext; Bereiche als „Folien 3 und 4 –
 * Inhalt“ oder „Folien 46–57 – Titel“; Aufzählungen mit „* 2026: …“ für
 * einen Bereich; eine „Kurzfassung, falls die Folien 46–57 übersprungen
 * werden: …“. Überschriften wie „Finanzplanung (Folien 43–59)“ gliedern nur.
 *
 * Regeln, die sich nicht erraten lassen:
 * - Der Text eines Bereichs steht auf seiner **ersten** Folie; die übrigen
 *   bleiben stumm (werden nur gezeigt).
 * - Aufzählungspunkte eines Bereichs werden verteilt: Hat der Bereich genau
 *   zwei Folien je Punkt (Kosten- und Umsatzvorschau je Jahr), bekommt die
 *   **erste** Folie jedes Paars einen Punkt; hat er genau eine Folie je Punkt,
 *   jede Folie einen; sonst bleiben alle Punkte auf der ersten Folie.
 * - Markdown-Links werden auf ihren Text reduziert („[www.k4ia.ai](https://…)“
 *   → „www.k4ia.ai“).
 * Alles lässt sich im Admin je Folie nachbessern.
 */
export interface SkriptImport {
  folien: Array<{ nr: number; titel: string; text: string }>
  kurzfassung: string | null
  quartale: { von: number; bis: number } | null
  /** Zeilen, die keiner Folie zugeordnet werden konnten — zum Nachsehen. */
  verworfen: string[]
}

const FOLIE_KOPF =
  /^Folien?\s+(\d+)(?:\s*(?:[–—-]|bis)\s*(\d+)|\s+und\s+(\d+))?\s*[–—-]\s*(.+?)\s*$/i
const GLIEDERUNG = /\(Folien?\s+\d+\s*[–—-]\s*\d+\)\s*$/i
const KURZFASSUNG = /^Kurzfassung\b[^:]*:\s*(.*)$/i
const BEREICH = /Folien?\s+(\d+)\s*[–—-]\s*(\d+)/i
const AUFZAEHLUNG = /^[*•\-–]\s+(.+)$/

function glatt(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[ \t]+/g, ' ')
    .trim()
}

export function parsePraesentationSkript(roh: string): SkriptImport {
  const folien = new Map<number, { nr: number; titel: string; text: string }>()
  const verworfen: string[] = []
  let kurzfassung: string | null = null
  let quartale: { von: number; bis: number } | null = null

  type Abschnitt = { von: number; bis: number; titel: string; absaetze: string[]; punkte: string[] }
  let aktuell: Abschnitt | null = null

  const schliessen = () => {
    if (!aktuell) return
    const { von, bis, titel, absaetze, punkte } = aktuell
    const anzahl = bis - von + 1
    for (let nr = von; nr <= bis; nr++) {
      if (!folien.has(nr)) folien.set(nr, { nr, titel, text: '' })
    }
    const setzen = (nr: number, text: string) => {
      const f = folien.get(nr)!
      f.text = f.text ? `${f.text}\n${text}` : text
    }
    if (absaetze.length > 0) setzen(von, absaetze.join('\n'))
    if (punkte.length > 0) {
      const schritt = anzahl === punkte.length ? 1 : anzahl === 2 * punkte.length ? 2 : 0
      punkte.forEach((p, i) => setzen(schritt === 0 ? von : von + i * schritt, p))
    }
    aktuell = null
  }

  for (const zeileRoh of roh.replace(/\r\n?/g, '\n').split('\n')) {
    const zeile = glatt(zeileRoh)
    if (!zeile) continue
    const kurz = KURZFASSUNG.exec(zeile)
    if (kurz) {
      schliessen()
      kurzfassung = glatt(kurz[1] ?? '')
      // „… falls die Folien 46–57 übersprungen werden“ nennt den Bereich selbst.
      const bereich = BEREICH.exec(zeile.slice(0, zeile.indexOf(':')))
      if (bereich) quartale = { von: Number(bereich[1]), bis: Number(bereich[2]) }
      continue
    }
    const kopf = FOLIE_KOPF.exec(zeile)
    if (kopf) {
      schliessen()
      const von = Number(kopf[1])
      const bis = kopf[2] ? Number(kopf[2]) : kopf[3] ? Number(kopf[3]) : von
      aktuell = {
        von,
        bis: Math.max(von, bis),
        titel: glatt(kopf[4] ?? ''),
        absaetze: [],
        punkte: [],
      }
      // Ein großer Bereich ist der, der übersprungen werden darf — falls die
      // Kurzfassung ihn nicht selbst nennt.
      if (!quartale && bis - von + 1 >= 6) quartale = { von, bis }
      continue
    }
    if (GLIEDERUNG.test(zeile)) continue
    if (!aktuell) {
      verworfen.push(zeile)
      continue
    }
    const punkt = AUFZAEHLUNG.exec(zeile)
    if (punkt) aktuell.punkte.push(glatt(punkt[1] ?? ''))
    else aktuell.absaetze.push(zeile)
  }
  schliessen()

  return {
    folien: [...folien.values()].sort((a, b) => a.nr - b.nr),
    kurzfassung,
    quartale,
    verworfen,
  }
}

/**
 * Eine Ansage (Flo, 31.10.): Titel und der Text, den K4ia unter
 * `/ansage/<id>` beim Antippen wörtlich spricht — als Tonspur wie bei
 * Intro und Präsentation, nichts formuliert das Modell.
 */
export const TIPP_GEWINNER = ['a', 'b', 'unentschieden'] as const
export type TippGewinner = (typeof TIPP_GEWINNER)[number]

/**
 * Die Auslosung nach der Ansage (Flo, 31.10.): K4ia „lost aus“, wer ein
 * Spiel gewinnt — das Ergebnis steht hier fest, die Trommel auf der Seite
 * landet immer darauf. Logos lädt das Admin je Mannschaft hoch.
 */
export const ansageTippSchema = z.object({
  aktiv: z.boolean().default(false),
  teamA: z.string().trim().max(80).default(''),
  teamB: z.string().trim().max(80).default(''),
  gewinner: z.enum(TIPP_GEWINNER).default('a'),
  /** Der Spielstand, wie er angezeigt wird („2:1“); leer = nur der Sieger. */
  ergebnis: z.string().trim().max(20).default(''),
  /** Was K4ia nach der Auslosung sagt; leer = nichts. */
  text: z.string().trim().max(1000).default(''),
})
export type AnsageTipp = z.infer<typeof ansageTippSchema>

export const ansageInputSchema = z.object({
  title: z.string().trim().max(200).default(''),
  text: z.string().trim().min(1, 'Text fehlt').max(5000),
  tipp: ansageTippSchema.default({}),
})
export type AnsageInput = z.infer<typeof ansageInputSchema>
